<template>
  <div
    class="form-field"
    :data-cy="field.conditions ? 'conditional-field' : 'non-conditional-field'"
  >
    <div class="d-flex flex-column my-2">
      <label
        :class="{ 'field-label-required': showInvalidMessage }"
        class="d-block flex-2 mr-2 font-weight-bold"
        v-if="displayLabelAbove && !displayLabelBeside"
      >
        <span v-html="field.label" />
      </label>
      <div class="flex-3">
        <h4 v-if="fieldType === 'text'" :class="field.class">
          {{ field.label }}
        </h4>
        <CInputRadioGroup
          data-cy="radio-group"
          v-else-if="fieldType === 'radio'"
          :class="[
            !displayInColumn
              ? 'form-group radio-group'
              : 'form-group column-group',
          ]"
          :style="optionsStyle"
          :key="`${field.name}_${numberOfOptions}_${answersKey}`"
          :checked.sync="content"
          :inline="!displayInColumn"
          v-bind="field"
          :options="getOptions"
          :selectedOptionClasses="highlightAnswer ? 'blue-rectangle' : ''"
          @invalid="(e) => handleInvalid(e, $t('missing_field_error_message'))"
          @update:checked="(v, e) => handleGroupInput(e)"
        />
        <template v-else-if="fieldType === 'checkbox'">
          <CInputCheckboxGroup
            custom
            data-cy="checkbox-group"
            v-if="field.options"
            :key="`${field.name}_${numberOfOptions}_${answersKey}`"
            class="form-group"
            :checked.sync="content"
            :inline="!isColumnDisplayField"
            v-bind="field"
            @invalid="
              (e) => handleInvalid(e, $t('missing_checkbox_error_popup'))
            "
            @update:checked="(v, e) => handleInput(e)"
            :disabledOptions="getDisabledOptions()"
          />
          <div v-else class="d-flex">
            <CInputCheckbox
              custom
              :checked.sync="content"
              inline
              v-bind="field"
              :label="null"
              @invalid="
                (e) => handleInvalid(e, $t('missing_checkbox_error_popup'))
              "
              @update:checked="(v, e) => handleInput(e)"
              ref="singleCheckbox"
            />
            <div class="pointer" @click="handleCheckboxLabelClick">
              <span v-html="field.label" />
            </div>
          </div>
        </template>
        <div
          :class="[
            field.label.length > 12
              ? 'd-flex flex-column align-items-start mr-2'
              : 'd-flex flex-row align-items-center mr-2',
          ]"
          v-else-if="
            fieldType === 'textbox' ||
            'int' ||
            'dropdown' ||
            'date'
          "
        >
          <label
            :class="{ 'field-label-required': showInvalidMessage }"
            class="d-block flex-1 font-weight-bold mr-1"
            v-if="displayLabelBeside"
          >
            <div v-html="field.label" />
          </label>
          <MzDatePicker
            data-cy='datepicker'
            :value.sync="content"
            v-if="fieldType === 'date'"
            class="w-100"
            :required="field.required"
            @update:value="showInvalidMessage = false"
          />
          <div v-if="fieldType === 'dropdown'" :data-cy="field.name" class="w-100">
            <MzSelect
              :name="field.name"
              :key="`${field.name}_${numberOfOptions}_${answersKey}`"
              searchable
              allow-unselect
              class="w-100"
              :required="field.required"
              :options="getOptions"
              :value.sync="content"
              @update:value="showInvalidMessage = false;"
              @invalid="
                (e) => handleInvalid(e, $t('missing_field_error_message'))
              "
            />
          </div>
          <template v-if="fieldType === 'textbox'">
            <CInput
              v-bind="field"
              v-model="content"
              label=""
              type="text"
              class="w-100 justify-content-start"
              @invalid="
              (e) => handleInvalid(e, $t('missing_field_error_message'))
            "
              @change="(v, e) => handleInput(v,e)"
            />
          </template>
          <CInput
            v-else-if="fieldType === 'int'"
            type="number"
            :min="0"
            :max="999999999999999"
            :decimal="0"
            v-bind="field"
            label=""
            v-model="content"
            class="w-100 justify-content-start"
            @invalid="
              (e) => handleInvalid(e, $t('missing_field_error_message'))
            "
            @change="(v, e) => handleInput(v,e)"
            @increment="(v) => handleInput(v)"
            @decrement="(v) => handleInput(v)"
          />
        </div>
      </div>
    </div>
    <div
      v-if="showInvalidMessage"
      class="text-danger ml-3"
      :style="validationMessageStyle"
    >
      {{ $t("missing_field_error_message") }}
    </div>
  </div>
</template>

<script>
export default {
  name: "FormField",
  data() {
    return {
      longInputTypes: ['textbox', 'int'],
      content: this.value || null,
      showInvalidMessage: false,
      selectedPcsCategory1: "",
      selectedPcsCategory2: "",
      selectedBacCategory: "",
      category: {
        "pcs_parent_1": "pcs_category_parent_1",
        "pcs_parent_2": "pcs_category_parent_2",
        "type_bac": "bac_category",
      }

    }
  },
  props: {
    field: Object,
    answer: Object,
    answerCopy: Object,
    values: String,
    value: [String, Array, Number, Boolean],
    disabled: Boolean,
    isEdit: Boolean,
    highlightAnswer: Boolean,
    columnFieldNames: {
      type: Array,
      default(){return ['bac_specialite', "secteur_activite"]},
    }
  },
  computed: {
    fieldType() {
      return this.field.type.toLowerCase();
    },
    getOptions() {
      if(['pcs_parent_1', 'pcs_parent_2', 'type_bac'].includes(this.field.name)) {
        return this.field.options[
            this.answerCopy[this.category[this.field.name]]
            ] || []
      }
      return this.field.options
    },
    answersKey(){
      return Array.isArray(this.content) ? this.content.join(',') : this.content;
    },
    numberOfOptions(){
      return this.field?.options?.length || 0
    },
    isColumnDisplayField(){
      return this.columnFieldNames.includes(this.field.name)
    },
    validationMessageStyle() {
      return {
        "margin-top":
          this.fieldType === "radio" ? "-20px" : "-10px",
      };
    },
    displayLabelAbove() {
      return this.fieldType !== "text";
    },
    displayLabelBeside() {
      return (
        (this.fieldType === "textbox" || "int") &&
        !this.field.options
      );
    },
    displayInColumn() {
      return (
        this.numberOfOptions <= 5 || this.isColumnDisplayField
      );
    },
    optionsStyle() {
      return {
        "grid-auto-flow": "column",
        "grid-template-rows": `repeat(${Math.ceil(
          this.numberOfOptions / 3
        )}, 1fr)`,
      };
    },
  },
  watch: {
    content() {
      if (!this.longInputTypes.includes(this.fieldType)){
        this.$emit("input", this.content);
      }
    },
  },
  methods: {
    getDisabledOptions() {
      if (this.field.name === 'bac_specialite') {
        if (this.content && this.content.length === 2) {
          return this.field.options.filter((f) => !this.content.includes(f.value)).map((f) => f.value)
        }
      }
      return []
    },
    handleFormSubmit() {
      if (this.fieldType === 'date' && this.field.required) {
        if (this.content === null) this.showInvalidMessage = true
      }
    },
    handleInvalid(e, validationError) {
      if (validationError) e.target.setCustomValidity(validationError);
      this.showInvalidMessage = true;
    },
    handleGroupInput(e) {
      if (e.target.parentElement.parentElement.children[0]) {
        e.target.parentElement.parentElement.children[0].setCustomValidity("");
      }
      this.showInvalidMessage = false;
    },
    handleInput(v,e) {
      if (this.longInputTypes.includes(this.fieldType)){
        this.content = v
        this.$emit('input', this.content)
      }
      if (e?.target) e.target.setCustomValidity("");
      this.showInvalidMessage = false;
    },
    handleCheckboxLabelClick() {
      if (!this.field.disabled) this.content = !this.content;
      this.$refs.singleCheckbox.$el
        .getElementsByTagName("input")[0]
        .setCustomValidity("");
      this.showInvalidMessage = false;
    },
  },
};
</script>

<style lang="scss">
.field-label-required {
  position: relative;

  &:before {
    position: absolute;
    transform: translateX(-0.6rem);
    content: "*";
    color: $danger;
    font-size: 1.2rem;
  }
}

.blue-rectangle {
  position: relative;

  &::after {
    content: "";
    position: absolute;
    width: calc(100% + 8px);
    transform: translateX(-1px);
    left: 0;
    height: 100%;
    padding: 0 5px;
    box-shadow: 0 0 0 2px $primary;
    border-radius: 2px;
  }
}

.form-field {
  .custom-control-input {
    opacity: 1 !important;
  }
  label, .custom-control-label {
    font-size: 0.875rem;
    color: rgb(70, 75, 75) !important;
  }

  .custom-control-label {font-weight: normal !important;}

  .mz-select, .mz-input{
    border-color: #d8dbe0 !important;
  }
}

.radio-group,
.form-check {
  input,
  label {
    cursor: pointer;
  }
}

.radio-group {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;

  div.form-check {
    justify-content: flex-start;
  }
}
.input-group {
  display: flex;
  flex-direction: row;
  .form-control {
    width: 1500px;
    float: left;
  }
  div {
    float: left;
    text-align: left;
  }
}

.column-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
@media (max-width: map-get($container-max-widths, "sm")) {
  .radio-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
